import { Move } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VehicleDetails from './VehicleDetails';
import { liveVehicleApi } from './liveVehicleApi';
import { mapDeviceListItemToPatrol } from './liveVehicleMappers';
import { LIVE_VEHICLE_MAP_ICON_URL } from './liveVehicleSeed';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyA2tidsBVB4Kn3Ev4hf84hCsbSCSjFXKsI';

const DEFAULT_CENTER = { lat: 17.91, lng: 79.05 };
const DEFAULT_ZOOM = 7;

const verifyGoogleMapsLoaded = () =>
  Boolean(
    window.google &&
      window.google.maps &&
      window.google.maps.MapTypeId &&
      window.google.maps.ControlPosition
  );

/**
 * Live vehicle map with list / detail panel on the right.
 */
const LiveVehicleDashboard = () => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const didFitVehicleBoundsRef = useRef(false);

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [mapLoadError, setMapLoadError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [patrols, setPatrols] = useState([]);
  const [selectedPatrolId, setSelectedPatrolId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [listError, setListError] = useState(null);
  const [isListLoading, setIsListLoading] = useState(true);

  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 768
  );

  const fetchVehicles = useCallback(async () => {
    setListError(null);
    try {
      const data = await liveVehicleApi.getAllLiveVehicles({
        includePosition: true,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setPatrols(items.map(mapDeviceListItemToPatrol));
      didFitVehicleBoundsRef.current = false;
    } catch (error) {
      console.error('Failed to load live vehicles:', error);
      setListError(error?.message ?? 'Failed to load vehicles.');
      setPatrols([]);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setIsMobileViewport(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsListLoading(true);
      await fetchVehicles();
      if (!cancelled) setIsListLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchVehicles]);

  const loadGoogleMapsScript = useCallback(callback => {
    if (verifyGoogleMapsLoaded()) {
      setIsGoogleMapsLoaded(true);
      callback();
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const checkGoogleMaps = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(checkGoogleMaps);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const verifyInterval = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(verifyInterval);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 50);

      setTimeout(() => {
        clearInterval(verifyInterval);
        if (verifyGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
          callback();
        } else {
          setMapLoadError('Google Maps did not initialize in time.');
        }
      }, 5000);
    };
    script.onerror = () => {
      setMapLoadError('Failed to load Google Maps.');
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    loadGoogleMapsScript(() => {});
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapContainerRef.current || !window.google) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_TOP,
        },
      });
    }

    return () => {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
      mapInstanceRef.current = null;
      didFitVehicleBoundsRef.current = false;
    };
  }, [isGoogleMapsLoaded]);

  const mappablePatrols = useMemo(
    () => patrols.filter(p => p.has_position && p.lat != null && p.lng != null),
    [patrols]
  );

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapInstanceRef.current || !window.google) return;

    const map = mapInstanceRef.current;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    mappablePatrols.forEach(p => {
      const lat = Number(p.lat);
      const lng = Number(p.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const position = { lat, lng };
      bounds.extend(position);

      const id = p.patrol_id ?? p.id;
      const isSelected = selectedPatrolId != null && id === selectedPatrolId;
      const size = isSelected ? 52 : 44;

      const marker = new window.google.maps.Marker({
        position,
        map,
        title: p.name ?? p.license_plate ?? String(id ?? ''),
        zIndex: isSelected ? 2 : 1,
        icon: {
          url: LIVE_VEHICLE_MAP_ICON_URL,
          scaledSize: new window.google.maps.Size(size, size),
          anchor: new window.google.maps.Point(size / 2, size / 2),
        },
      });

      marker.addListener('click', () => {
        if (id != null) setSelectedPatrolId(id);
      });

      markersRef.current.push(marker);
    });

    if (!bounds.isEmpty() && !didFitVehicleBoundsRef.current) {
      map.fitBounds(bounds);
      didFitVehicleBoundsRef.current = true;
    }
  }, [isGoogleMapsLoaded, mappablePatrols, selectedPatrolId]);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapInstanceRef.current || !window.google) return;
    if (selectedPatrolId == null) return;

    const p = patrols.find(x => (x.patrol_id ?? x.id) === selectedPatrolId);
    if (!p || p.lat == null || p.lng == null) return;

    const map = mapInstanceRef.current;
    map.panTo({ lat: Number(p.lat), lng: Number(p.lng) });
    const z = map.getZoom();
    if (z < 10) map.setZoom(10);
  }, [isGoogleMapsLoaded, selectedPatrolId, patrols]);

  const handleRecenter = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;

    if (mappablePatrols.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      mappablePatrols.forEach(p => {
        const lat = Number(p.lat);
        const lng = Number(p.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) bounds.extend({ lat, lng });
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
        return;
      }
    }

    map.panTo(DEFAULT_CENTER);
    map.setZoom(DEFAULT_ZOOM);
  }, [mappablePatrols]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchVehicles();
    setIsRefreshing(false);
  }, [fetchVehicles]);

  const handlePatrolSelect = useCallback(patrol => {
    const id = patrol?.patrol_id ?? patrol?.id ?? null;
    setSelectedPatrolId(id);
  }, []);

  const handlePatrolPositionUpdate = useCallback((patrolId, position) => {
    if (patrolId == null || !position?.has_position) return;
    setPatrols(prev =>
      prev.map(p => {
        const id = p.patrol_id ?? p.id;
        if (id !== patrolId) return p;
        return { ...p, ...position };
      })
    );
  }, []);

  const handlePatrolClose = useCallback(() => {
    setSelectedPatrolId(null);
  }, []);

  const selectedPatrol = useMemo(() => {
    if (selectedPatrolId == null) return null;
    return patrols.find(p => (p.patrol_id ?? p.id) === selectedPatrolId) ?? null;
  }, [patrols, selectedPatrolId]);

  return (
    <div className="relative flex h-full w-full min-h-0 flex-col">
      <div
        className={`flex min-h-0 w-full flex-1 gap-4 overflow-hidden ${
          isMobileViewport ? 'flex-col-reverse' : 'flex-row'
        }`}
      >
        <div
          className={`flex-shrink-0 overflow-hidden ${
            isMobileViewport ? 'w-full flex-1' : 'w-96'
          }`}
          style={isMobileViewport ? { minHeight: '300px' } : { height: '100%' }}
        >
          <VehicleDetails
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing || isListLoading}
            isListLoading={isListLoading}
            listError={listError}
            patrols={patrols}
            onPatrolSelect={handlePatrolSelect}
            onPatrolPositionUpdate={handlePatrolPositionUpdate}
            selectedPatrolId={selectedPatrolId}
            selectedVehicle={selectedPatrol}
            onBack={handlePatrolClose}
          />
        </div>

        <div
          className={`flex min-h-0 flex-col ${isMobileViewport ? 'w-full' : 'min-w-0 flex-1'}`}
          style={
            isMobileViewport
              ? { height: `${viewportWidth}px`, width: '100%' }
              : { height: '100%', width: '100%' }
          }
        >
          <div className="dashboard-card map-card relative h-full w-full flex-shrink-0 overflow-hidden rounded-xl shadow-xl">
            <div ref={mapContainerRef} className="absolute inset-0 h-full w-full" />

            {!isGoogleMapsLoaded && !mapLoadError && (
              <div className="absolute inset-0 z-[1] flex items-center justify-center bg-slate-100/90 text-sm text-slate-600">
                Loading map…
              </div>
            )}

            {mapLoadError && (
              <div className="absolute inset-0 z-[1] flex items-center justify-center bg-slate-100/95 px-4 text-center text-sm text-red-700">
                {mapLoadError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveVehicleDashboard;
